import React, { FormEvent } from 'react';
import { Eyebrow, PageHeading } from '../../../../design-system';
import styles from './AccountSection.module.css';

export interface AccountSectionProps {
  passwordForm: { current: string; next: string; confirm: string };
  passwordStatus: 'idle' | 'loading' | 'success' | 'error';
  passwordMessage: string | null;
  deleteAccountError: string | null;
  deleteAccountConfirm: boolean;
  deleteAccountLoading: boolean;
  onPasswordInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onPasswordSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void>;
  onDeleteAccountClick: () => void;
  onDeleteAccountCancel: () => void;
  onDeleteAccountConfirm: () => Promise<void>;
}

export const AccountSection: React.FC<AccountSectionProps> = ({
  passwordForm,
  passwordStatus,
  passwordMessage,
  deleteAccountError,
  deleteAccountConfirm,
  deleteAccountLoading,
  onPasswordInputChange,
  onPasswordSubmit,
  onDeleteAccountClick,
  onDeleteAccountCancel,
  onDeleteAccountConfirm,
}) => {
  return (
    <section className={`${styles.section} ${styles.accountSection}`}>
      <div className={styles.accountIntro}>
        <Eyebrow tone="default" dot>ACCOUNT</Eyebrow>
        <PageHeading
          accentWord="zone"
          accentTone="red"
          size="h2"
          align="left"
          level="h2"
        >
          Danger zone
        </PageHeading>
        <p className={styles.accountSubtitle}>
          Permanently remove your admin identity and revoke access
        </p>
      </div>
      <div className={styles.accountDangerPanel}>
        <div className={styles.accountDangerHeader}>
          <div>
            <p className={styles.accountDangerTitle}>Change admin password</p>
            <p className={styles.accountDangerText}>
              Keep your credentials secure by updating your password regularly.
            </p>
          </div>
        </div>
        <form className={styles.passwordForm} onSubmit={onPasswordSubmit}>
          <div className={styles.passwordFormRow}>
            <label>
              Current password
              <input
                type="password"
                name="current"
                value={passwordForm.current}
                onChange={onPasswordInputChange}
                placeholder="••••••••"
              />
            </label>
            <label>
              New password
              <input
                type="password"
                name="next"
                value={passwordForm.next}
                onChange={onPasswordInputChange}
                placeholder="At least 8 characters"
              />
            </label>
            <label>
              Confirm new password
              <input
                type="password"
                name="confirm"
                value={passwordForm.confirm}
                onChange={onPasswordInputChange}
                placeholder="Repeat new password"
              />
            </label>
          </div>
          {passwordMessage && (
            <p className={passwordStatus === 'error' ? styles.errorText : styles.successText}>
              {passwordMessage}
            </p>
          )}
          <button
            type="submit"
            className={styles.passwordSubmitButton}
            disabled={passwordStatus === 'loading'}
          >
            {passwordStatus === 'loading' ? 'Updating…' : 'Update password'}
          </button>
        </form>
      </div>
      <div className={styles.accountDangerPanel}>
        <div className={styles.accountDangerHeader}>
          <div>
            <p className={styles.accountDangerText}>
              Request deletion of your admin account by email. A confirmation link will be sent to
              your inbox, and the account will only be removed after you approve the request.
            </p>
          </div>
          <div className={styles.accountDangerBadge}>Irreversible</div>
        </div>
        <ul className={styles.accountChecklist}>
          <li>A confirmation link is sent to your registered email address.</li>
          <li>No data is removed until you confirm the deletion request.</li>
          <li>You will need a new invite to regain access after deletion.</li>
        </ul>
        {deleteAccountError && <p className={styles.accountNote}>{deleteAccountError}</p>}
        <div className={styles.accountDangerActions}>
          {deleteAccountConfirm ? (
            <>
              <button
                type="button"
                className={styles.accountDangerButton}
                onClick={onDeleteAccountConfirm}
                disabled={deleteAccountLoading}
              >
                {deleteAccountLoading ? 'Sending…' : 'Send confirmation email'}
              </button>
              <button
                type="button"
                className={styles.accountCancelButton}
                onClick={onDeleteAccountCancel}
                disabled={deleteAccountLoading}
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              className={styles.accountDangerGhost}
              onClick={onDeleteAccountClick}
            >
              Delete admin account
            </button>
          )}
        </div>
        {deleteAccountConfirm && <p className={styles.accountNote}>The account will only be deleted after the email confirmation link is approved.</p>}
      </div>
    </section>
  );
};
